import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Header from './Header';
import FoodSelector from './FoodSelector';
import MacroTargets from './MacroTargets';
import MealResults from './MealResults';
import CustomFoodForm from './CustomFoodForm';
import PaymentModal from './PaymentModal';
import FeedbackModal from './FeedbackModal';
import GetStartedGuide from './GetStartedGuide';
import AnalyticsModal from './AnalyticsModal';
import UserProfileForm from './UserProfileForm';
import { foodDatabase, getConversionFactor } from '../data/foodDatabase';
import usageTracker from '../services/usageTracker';
import { HashRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';

const { FiTarget, FiCalculator, FiCheckCircle, FiPlusCircle, FiStar, FiZap, FiMessageCircle, FiHelpCircle, FiBarChart, FiMenu, FiX, FiShare2, FiUser, FiSliders } = FiIcons;

const MealCalculator = () => {
  const [selectedFoods, setSelectedFoods] = useState([]);
  const [targets, setTargets] = useState({
    calories: '',
    protein: '',
    carbs: '',
    fat: ''
  });
  const [thresholds, setThresholds] = useState({
    calories: 10,
    protein: 0,
    carbs: 0,
    fat: 0,
    advancedMode: false
  });
  const [mealPlan, setMealPlan] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showCustomFoodForm, setShowCustomFoodForm] = useState(false);
  const [customFoods, setCustomFoods] = useState([]);

  // Usage tracking and payment states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [userUsage, setUserUsage] = useState(null);
  const [remainingCalculations, setRemainingCalculations] = useState(3);
  const [isLoadingUsage, setIsLoadingUsage] = useState(true);

  // New modal states
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showGetStartedGuide, setShowGetStartedGuide] = useState(false);
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // User authentication state
  const [user, setUser] = useState(null);

  // User profile form
  const [showUserProfileForm, setShowUserProfileForm] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [setupComplete, setSetupComplete] = useState(false);

  // Check for payment success from URL (Stripe redirect)
  const location = useLocation();

  useEffect(() => {
    const checkForPaymentSuccess = async () => {
      // Check if we're coming from a payment success redirect
      if (location.hash.includes('payment-success') || location.search.includes('session_id')) {
        console.log('Payment success detected, updating usage stats');
        
        // Small delay to ensure payment verification completes
        setTimeout(async () => {
          await updateUsageStats();
          console.log('Usage stats updated after payment');
        }, 1000);
      }
    };

    checkForPaymentSuccess();
  }, [location]);

  // Load user data on mount
  useEffect(() => {
    loadUserData();
    updateUsageStats();
  }, []);

  const loadUserData = () => {
    const userData = localStorage.getItem('meal_calculator_user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      console.log('Loaded user data:', parsedUser);
    }
  };

  // Load usage stats
  const updateUsageStats = async () => {
    setIsLoadingUsage(true);
    try {
      const usage = await usageTracker.getCurrentUserUsage();
      setUserUsage(usage);
      const remaining = await usageTracker.getRemainingFreeCalculations();
      setRemainingCalculations(remaining);
      
      console.log('Updated usage stats:', { usage, remaining });
    } catch (error) {
      console.error('Error loading usage stats:', error);
    } finally {
      setIsLoadingUsage(false);
    }
  };

  // Check if user has completed the profile setup
  useEffect(() => {
    const profileData = localStorage.getItem('user_profile');
    if (profileData) {
      setUserProfile(JSON.parse(profileData));
      setSetupComplete(true);

      // If user has a profile, auto-populate the targets
      const profile = JSON.parse(profileData);
      if (profile && profile.calculatedTargets) {
        setTargets(profile.calculatedTargets);

        // Set thresholds based on advanced mode - enable advanced mode by default
        if (profile.calculatedTargets.protein && profile.calculatedTargets.carbs && profile.calculatedTargets.fat) {
          setThresholds({
            calories: 10,
            protein: 15,
            carbs: 15,
            fat: 15,
            advancedMode: true
          });
        }
      }
    } else {
      // Show profile form for new users
      setShowUserProfileForm(true);
    }

    // Show get started guide for new users
    const hasSeenGuide = localStorage.getItem('meal_calculator_seen_guide');
    if (!hasSeenGuide) {
      setShowGetStartedGuide(true);
      localStorage.setItem('meal_calculator_seen_guide', 'true');
    }
  }, []);

  const calculateMeal = async () => {
    if (selectedFoods.length === 0 || !targets.calories) return;

    // Check if user needs to pay - only if they're not a premium user
    if (user) {
      // For logged-in users, check their specific usage
      const needsPayment = await usageTracker.needsPayment();
      if (needsPayment) {
        setShowPaymentModal(true);
        return;
      }
    } else {
      // For guest users, use the standard check
      const needsPayment = await usageTracker.needsPayment();
      if (needsPayment) {
        setShowPaymentModal(true);
        return;
      }
    }

    setIsCalculating(true);

    // Record calculation attempt
    await usageTracker.recordCalculationAttempt();

    // Simulate calculation delay for better UX
    setTimeout(() => {
      const result = optimizeMeal(selectedFoods, targets, thresholds);
      setMealPlan(result);
      setIsCalculating(false);
      setCurrentStep(3);

      // Check if calculation was successful and record it
      if (result && result.foods && result.foods.length > 0 && 
          usageTracker.isCalculationSuccessful(result, targets, thresholds)) {
        usageTracker.recordSuccessfulCalculation().then(updatedUsage => {
          setUserUsage(updatedUsage);
          updateUsageStats();
        });
      }
    }, 1500);
  };

  const handlePaymentSuccess = async () => {
    console.log('Payment success handler called');
    try {
      // Update usage stats immediately
      await updateUsageStats();
      
      // Close payment modal
      setShowPaymentModal(false);
      
      // Reload user data to get updated premium status
      loadUserData();
      
      console.log('Payment success handled successfully');
    } catch (error) {
      console.error('Error handling payment success:', error);
    }
  };

  const handleUserUpdate = (userData) => {
    setUser(userData);
    if (userData) {
      localStorage.setItem('meal_calculator_user', JSON.stringify(userData));
      console.log('User updated:', userData);
    } else {
      localStorage.removeItem('meal_calculator_user');
    }
    
    // Update usage stats when user changes
    updateUsageStats();
  };

  const roundToHalf = (value, servingUnit) => {
    // For countable items like eggs, round to nearest 0.5
    if (['egg', 'slice', 'piece', 'medium', 'large', 'small', 'fillet', 'patty', 'steak', 'thigh', 'head', 'handful', 'half', 'ball'].includes(servingUnit)) {
      return Math.round(value * 2) / 2; // Round to nearest 0.5
    }
    
    // For weight-based measurements, round to nearest whole number
    if (['g', 'oz', 'kg'].includes(servingUnit)) {
      return Math.round(value);
    }
    
    // For volume measurements, round to nearest 0.5
    return Math.round(value * 2) / 2;
  };

  // Updated optimization algorithm based on the provided Python script
  const optimizeMeal = (foods, targetMacros, thresholds) => {
    // Define target values
    const targetCals = parseFloat(targetMacros.calories);
    const targetProtein = parseFloat(targetMacros.protein) || 0;
    const targetCarbs = parseFloat(targetMacros.carbs) || 0;
    const targetFat = parseFloat(targetMacros.fat) || 0;

    // Define thresholds as decimal values
    const caloriesThreshold = thresholds.calories / 100;
    const proteinThreshold = thresholds.protein / 100;
    const carbsThreshold = thresholds.carbs / 100;
    const fatThreshold = thresholds.fat / 100;

    // Set maximum attempts and combinations
    const maxAttempts = 2000;
    const maxCombinations = 10;

    // Track valid combinations
    let validCombinations = [];
    let bestSolution = null;
    let bestScore = Infinity;

    // Use a flag to check if we should consider macros
    let macroFlag = thresholds.advancedMode;
    let attempts = 0;

    // Start with all selected foods
    let foodsToCalculate = foods.length;

    while (validCombinations.length < 1 && attempts < maxAttempts) {
      // Generate a potential meal combination
      const solution = foods.map(food => {
        // Get conversion factor for selected serving unit
        const conversionFactor = getConversionFactor(food, food.selectedServingUnit);
        
        // Generate random serving within desired range
        const minServing = food.desiredMin;
        const maxServing = food.desiredMax;
        const serving = Math.random() * (maxServing - minServing) + minServing;
        
        // Round to appropriate precision based on serving unit
        const roundedServing = roundToHalf(serving, food.selectedServingUnit);
        
        // Convert to base serving size for nutrition calculation
        const baseServingMultiplier = (roundedServing * conversionFactor) / food.servingSize;

        return {
          ...food,
          actualServingAmount: roundedServing,
          actualCalories: food.calories * baseServingMultiplier,
          actualProtein: food.protein * baseServingMultiplier,
          actualCarbs: food.carbs * baseServingMultiplier,
          actualFat: food.fat * baseServingMultiplier
        };
      });

      // Calculate totals
      const totals = solution.reduce((acc, item) => ({
        calories: acc.calories + item.actualCalories,
        protein: acc.protein + item.actualProtein,
        carbs: acc.carbs + item.actualCarbs,
        fat: acc.fat + item.actualFat
      }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

      // Check if the combination meets calorie threshold first
      const calorieError = Math.abs(totals.calories - targetCals) / targetCals;
      const withinCalorieThreshold = calorieError <= caloriesThreshold;

      if (withinCalorieThreshold) {
        // If we're not in advanced mode, or if we are in advanced mode and macros are within threshold
        if (!macroFlag || (
          (targetProtein === 0 || Math.abs(totals.protein - targetProtein) / targetProtein <= proteinThreshold) &&
          (targetCarbs === 0 || Math.abs(totals.carbs - targetCarbs) / targetCarbs <= carbsThreshold) &&
          (targetFat === 0 || Math.abs(totals.fat - targetFat) / targetFat <= fatThreshold)
        )) {
          // Calculate score based on how close we are to targets
          let score = calorieError;
          
          if (macroFlag) {
            if (targetProtein > 0) {
              const proteinError = Math.abs(totals.protein - targetProtein) / targetProtein;
              score += proteinError;
            }
            if (targetCarbs > 0) {
              const carbsError = Math.abs(totals.carbs - targetCarbs) / targetCarbs;
              score += carbsError;
            }
            if (targetFat > 0) {
              const fatError = Math.abs(totals.fat - targetFat) / targetFat;
              score += fatError;
            }
          }

          validCombinations.push({
            foods: solution,
            totals,
            score
          });

          // Keep track of the best solution
          if (score < bestScore) {
            bestScore = score;
            bestSolution = {
              foods: solution,
              totals,
              score
            };
          }
        }
      }

      attempts++;

      // If we've tried many times and still don't have a valid combination, adjust strategy
      if (attempts >= maxAttempts / 2 && validCombinations.length === 0 && foodsToCalculate > foods.length / 2) {
        // Try with fewer foods
        foodsToCalculate--;
        attempts = 0;
      } else if (attempts >= maxAttempts && validCombinations.length === 0 && macroFlag) {
        // If we still don't have a valid combination, disable macro checking
        macroFlag = false;
        foodsToCalculate = foods.length;
        attempts = 0;
      }
    }

    // If we found valid combinations, return the best one
    if (validCombinations.length > 0) {
      // Sort by score to get the best combinations
      validCombinations.sort((a, b) => a.score - b.score);
      return validCombinations[0]; // Return the best combination
    }

    // If we didn't find any valid combinations, return a failure result
    return {
      foods: [],
      totals: { calories: 0, protein: 0, carbs: 0, fat: 0 },
      score: Infinity,
      failed: true
    };
  };

  const resetCalculator = () => {
    setSelectedFoods([]);
    setTargets({
      calories: '',
      protein: '',
      carbs: '',
      fat: ''
    });
    setThresholds({
      calories: 10,
      protein: 0,
      carbs: 0,
      fat: 0,
      advancedMode: false
    });
    setMealPlan(null);
    setCurrentStep(1);

    // If user has a profile, repopulate the targets
    if (userProfile && userProfile.calculatedTargets) {
      setTargets(userProfile.calculatedTargets);

      // Set thresholds based on advanced mode - enable advanced mode by default
      if (userProfile.calculatedTargets.protein && userProfile.calculatedTargets.carbs && userProfile.calculatedTargets.fat) {
        setThresholds({
          calories: 10,
          protein: 15,
          carbs: 15,
          fat: 15,
          advancedMode: true
        });
      }
    }
  };

  const handleAddCustomFood = (newFood) => {
    setCustomFoods(prev => [...prev, newFood]);
    setShowCustomFoodForm(false);
  };

  const canCalculate = selectedFoods.length > 0 && targets.calories;

  const handleMealPlanUpdate = (updatedMealPlan) => {
    setMealPlan(updatedMealPlan);
  };

  const handleAddRecommendedFood = (foodToAdd) => {
    // Add the recommended food to selectedFoods
    setSelectedFoods(prevFoods => [...prevFoods, foodToAdd]);

    // Recalculate the meal plan
    setTimeout(() => {
      calculateMeal();
    }, 500);
  };

  const handleRemoveFood = (foodId) => {
    // Remove the food from meal plan
    if (mealPlan && mealPlan.foods) {
      const updatedFoods = mealPlan.foods.filter(food => food.id !== foodId);
      
      // Recalculate totals
      const newTotals = updatedFoods.reduce((acc, item) => ({
        calories: acc.calories + item.actualCalories,
        protein: acc.protein + item.actualProtein,
        carbs: acc.carbs + item.actualCarbs,
        fat: acc.fat + item.actualFat
      }), { calories: 0, protein: 0, carbs: 0, fat: 0 });

      // Update meal plan
      setMealPlan({
        ...mealPlan,
        foods: updatedFoods,
        totals: newTotals
      });

      // Also remove from selected foods
      setSelectedFoods(prevFoods => prevFoods.filter(food => food.id !== foodId));
    }
  };

  const handleProfileSave = (profileData) => {
    setUserProfile(profileData);
    setShowUserProfileForm(false);
    setSetupComplete(true);

    // Save to localStorage
    localStorage.setItem('user_profile', JSON.stringify(profileData));

    // Auto-populate targets based on profile
    if (profileData.calculatedTargets) {
      setTargets(profileData.calculatedTargets);

      // Set thresholds based on advanced mode - enable advanced mode by default
      if (profileData.calculatedTargets.protein && profileData.calculatedTargets.carbs && profileData.calculatedTargets.fat) {
        setThresholds({
          calories: 10,
          protein: 15,
          carbs: 15,
          fat: 15,
          advancedMode: true
        });
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <Header user={user} onUserUpdate={handleUserUpdate} />

      <div className="py-8 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Main Title */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <div className="flex items-center justify-center gap-3 mb-4">
              <SafeIcon icon={FiTarget} className="text-4xl text-indigo-600" />
              <h1 className="text-4xl font-bold text-gray-800">CreateMyMeal</h1>
            </div>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Select foods with custom serving ranges, set macro targets with flexible thresholds, 
              and get optimized meal plans tailored to your preferences.
            </p>

            {/* Usage Counter */}
            <div className="mt-4 flex items-center justify-center gap-4">
              {isLoadingUsage ? (
                <div className="bg-gray-100 text-gray-600 px-4 py-2 rounded-full flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-500 border-t-transparent"></div>
                  <span>Loading usage stats...</span>
                </div>
              ) : userUsage?.hasPaid ? (
                <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full flex items-center gap-2">
                  <SafeIcon icon={FiStar} className="text-lg" />
                  <span className="font-semibold">Pro User - Unlimited Access</span>
                </div>
              ) : (
                <div className="bg-orange-100 text-orange-800 px-4 py-2 rounded-full flex items-center gap-2">
                  <SafeIcon icon={FiZap} className="text-lg" />
                  <span className="font-semibold">
                    {remainingCalculations} successful calculation{remainingCalculations !== 1 ? 's' : ''} remaining
                  </span>
                </div>
              )}
            </div>
          </motion.div>

          {/* Description */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            {/* Action Buttons */}
            <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
              <button
                onClick={() => setShowUserProfileForm(true)}
                className="flex items-center gap-2 bg-white text-indigo-600 px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 border border-indigo-200"
              >
                <SafeIcon icon={FiUser} className="text-lg" />
                <span>{userProfile ? 'Edit Profile' : 'Create Profile'}</span>
              </button>

              <button
                onClick={() => setShowGetStartedGuide(true)}
                className="flex items-center gap-2 bg-white text-indigo-600 px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 border border-indigo-200"
              >
                <SafeIcon icon={FiHelpCircle} className="text-lg" />
                <span>Get Started Guide</span>
              </button>

              <button
                onClick={() => setShowAnalyticsModal(true)}
                className="flex items-center gap-2 bg-white text-indigo-600 px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 border border-indigo-200"
              >
                <SafeIcon icon={FiBarChart} className="text-lg" />
                <span>Analytics</span>
              </button>

              <button
                onClick={() => setShowFeedbackModal(true)}
                className="flex items-center gap-2 bg-white text-indigo-600 px-4 py-2 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 border border-indigo-200"
              >
                <SafeIcon icon={FiMessageCircle} className="text-lg" />
                <span>Feedback</span>
              </button>
            </div>
          </motion.div>

          {/* Progress Steps */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-center mb-8"
          >
            <div className="flex items-center space-x-4">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                      currentStep >= step
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {step}
                  </div>
                  {step < 3 && (
                    <div
                      className={`w-16 h-1 mx-2 ${
                        currentStep > step ? 'bg-indigo-600' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left Column - Food Selection & Targets */}
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-semibold text-gray-700">Food Selection</h3>
                <button
                  onClick={() => setShowCustomFoodForm(true)}
                  className="flex items-center gap-1 bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiPlusCircle} className="text-lg" />
                  <span>Add Custom Food</span>
                </button>
              </div>

              <FoodSelector
                selectedFoods={selectedFoods}
                setSelectedFoods={setSelectedFoods}
                onStepComplete={() => setCurrentStep(Math.max(currentStep, 2))}
                customFoods={customFoods}
              />

              <MacroTargets
                targets={targets}
                setTargets={setTargets}
                thresholds={thresholds}
                setThresholds={setThresholds}
                disabled={false} // Always allow editing after profile setup
              />

              {/* Calculate Button */}
              <motion.button
                whileHover={{ scale: canCalculate ? 1.02 : 1 }}
                whileTap={{ scale: canCalculate ? 0.98 : 1 }}
                onClick={calculateMeal}
                disabled={!canCalculate || isCalculating}
                className={`w-full py-4 px-6 rounded-xl font-semibold text-lg transition-all duration-300 ${
                  canCalculate
                    ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg hover:shadow-xl'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <div className="flex items-center justify-center gap-3">
                  {isCalculating ? (
                    <>
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent" />
                      <span>Optimizing...</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiSliders} className="text-xl" />
                      <span>Optimize My Meal</span>
                    </>
                  )}
                </div>
              </motion.button>

              {mealPlan && (
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={resetCalculator}
                  className="w-full py-3 px-6 rounded-xl font-semibold bg-gray-200 hover:bg-gray-300 text-gray-700 transition-all duration-300"
                >
                  Start New Calculation
                </motion.button>
              )}
            </div>

            {/* Right Column - Results */}
            <div>
              <AnimatePresence mode="wait">
                {mealPlan ? (
                  <MealResults
                    key="results"
                    mealPlan={mealPlan}
                    targets={targets}
                    thresholds={thresholds}
                    onMealPlanUpdate={handleMealPlanUpdate}
                    selectedFoods={selectedFoods}
                    onAddFood={handleAddRecommendedFood}
                    onRemoveFood={handleRemoveFood}
                  />
                ) : (
                  <motion.div
                    key="placeholder"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="bg-white rounded-2xl shadow-lg p-8 text-center"
                  >
                    <SafeIcon icon={FiCheckCircle} className="text-6xl text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">
                      Ready to Calculate
                    </h3>
                    <p className="text-gray-500">
                      Select foods with desired amounts and set your targets to generate your optimized meal plan.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Food Form Modal */}
      <AnimatePresence>
        {showCustomFoodForm && (
          <CustomFoodForm
            onClose={() => setShowCustomFoodForm(false)}
            onAddFood={handleAddCustomFood}
            nextId={foodDatabase.length + customFoods.length + 1}
          />
        )}
      </AnimatePresence>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onPaymentSuccess={handlePaymentSuccess}
        remainingCalculations={remainingCalculations}
        user={user}
        onUserUpdate={handleUserUpdate}
      />

      {/* Feedback Modal */}
      <AnimatePresence>
        {showFeedbackModal && (
          <FeedbackModal
            isOpen={showFeedbackModal}
            onClose={() => setShowFeedbackModal(false)}
          />
        )}
      </AnimatePresence>

      {/* Get Started Guide */}
      <AnimatePresence>
        {showGetStartedGuide && (
          <GetStartedGuide
            isOpen={showGetStartedGuide}
            onClose={() => setShowGetStartedGuide(false)}
          />
        )}
      </AnimatePresence>

      {/* Analytics Modal */}
      <AnimatePresence>
        {showAnalyticsModal && (
          <AnalyticsModal
            isOpen={showAnalyticsModal}
            onClose={() => setShowAnalyticsModal(false)}
          />
        )}
      </AnimatePresence>

      {/* User Profile Form */}
      <AnimatePresence>
        {showUserProfileForm && (
          <UserProfileForm
            isOpen={showUserProfileForm}
            onClose={() => {
              if (setupComplete) {
                setShowUserProfileForm(false);
              }
            }}
            onSave={handleProfileSave}
            initialProfile={userProfile}
            allowSkip={setupComplete}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default MealCalculator;